import React, { useMemo } from 'react';
import { motion } from 'framer-motion';
import { UsageEventsTracker } from '../../../services/UsageEventsTracker';

/**
 * Final step — Onboarding Complete
 * Celebration screen with summary and quick links.
 * Aggregates stats across all created courses.
 */
export default function CompleteStep({ data, onComplete }) {
  const courses = useMemo(() => (data.courses || []).filter(Boolean), [data.courses]);

  // Aggregate stats across all courses
  const stats = useMemo(() => {
    if (courses.length === 0) {
      return {
        sessionCount: data.sessionCount || 0,
        assignmentCount: data.assignmentCount || 0,
        docCount: data.uploadedDocIds?.length || 0,
      };
    }
    let sessions = 0, assignments = 0, docs = 0;
    for (const c of courses) {
      sessions += c.sessionCount || 0;
      assignments += c.assignmentCount || 0;
      docs += c.uploadedDocIds?.length || 0;
    }
    // Also count global uploaded docs not tracked per-course
    const globalDocs = data.uploadedDocIds?.length || 0;
    return {
      sessionCount: sessions,
      assignmentCount: assignments,
      docCount: Math.max(docs, globalDocs),
    };
  }, [courses, data]);

  const hasStats = stats.sessionCount > 0 || stats.assignmentCount > 0 || stats.docCount > 0 || data.flashcards?.length > 0;

  return (
    <div className="onboarding-card onboarding-complete-step">
      <motion.div
        className="onboarding-complete-emoji"
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        transition={{ type: 'spring', stiffness: 200, damping: 10, delay: 0.2 }}
      >
        🎉
      </motion.div>

      <div className="onboarding-card-header">
        <h2>You're all set!</h2>
        {courses.length > 1 ? (
          <p>
            <strong>{courses.length} courses</strong> created and ready to go:
          </p>
        ) : (
          <p>
            {data.courseName
              ? <><strong>{data.courseName}</strong> is ready to go.</>
              : 'Your account is set up and ready to use.'}
          </p>
        )}
      </div>

      {/* List course names for multi-course */}
      {courses.length > 1 && (
        <ul className="onboarding-complete-course-list">
          {courses.map((c, idx) => (
            <li key={c.courseId || idx}>{c.courseName || `Course ${idx + 1}`}</li>
          ))}
        </ul>
      )}

      {/* Stats summary */}
      {hasStats && (
        <div className="onboarding-complete-stats">
          {courses.length > 0 && (
            <div className="onboarding-complete-stat">
              <div className="onboarding-complete-stat-value">{courses.length}</div>
              <div className="onboarding-complete-stat-label">Course{courses.length > 1 ? 's' : ''}</div>
            </div>
          )}
          {stats.sessionCount > 0 && (
            <div className="onboarding-complete-stat">
              <div className="onboarding-complete-stat-value">{stats.sessionCount}</div>
              <div className="onboarding-complete-stat-label">Class Sessions</div>
            </div>
          )}
          {stats.assignmentCount > 0 && (
            <div className="onboarding-complete-stat">
              <div className="onboarding-complete-stat-value">{stats.assignmentCount}</div>
              <div className="onboarding-complete-stat-label">Assignments</div>
            </div>
          )}
          {stats.docCount > 0 && (
            <div className="onboarding-complete-stat">
              <div className="onboarding-complete-stat-value">{stats.docCount}</div>
              <div className="onboarding-complete-stat-label">Documents</div>
            </div>
          )}
          {data.flashcards?.length > 0 && (
            <div className="onboarding-complete-stat">
              <div className="onboarding-complete-stat-value">{data.flashcards.length}</div>
              <div className="onboarding-complete-stat-label">Flashcards</div>
            </div>
          )}
        </div>
      )}

      {data.courseCount > courses.length && courses.length > 0 && (
        <p style={{ fontSize: '14px', color: 'var(--semantic-text-secondary)', textAlign: 'center' }}>
          You mentioned {data.courseCount} courses — you can add more from the dashboard anytime.
        </p>
      )}

      <div className="onboarding-quick-links">
        <button className="onboarding-btn primary" onClick={() => {
          UsageEventsTracker.recordUserAction('ONBOARDING_INTERACTION', {
            action: 'quick_link_click',
            category: 'onboarding',
            label: 'complete',
            metadata: { destination: 'dashboard' },
          });
          onComplete();
        }}>
          Go to Dashboard →
        </button>
      </div>
    </div>
  );
}
